import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Button,
    Checkbox,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Chip,
    CircularProgress,
    Typography,
    IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Close } from "@mui/icons-material";
import useBatchAddJira from "../../hooks/components/useBatchAddJira";
import SelectInput, { SelectItem } from "../inputs/SelectInput";
import Text from "../Text";
import { TextSize } from "../utils/TextSize";

type BatchAddJiraModalProps = {
    open: boolean;
    onClose: () => void;
};

const BatchAddJiraModal = ({ open, onClose }: BatchAddJiraModalProps) => {
    const {
        sprints,
        sprintsLoading,
        selectedSprint,
        setSelectedSprint,
        searchResults,
        selectedKeys,
        searchLoading,
        submitting,
        toggleSelect,
        toggleSelectAll,
        submit,
    } = useBatchAddJira(onClose);

    const sprintOptions: SelectItem[] = sprints.map((sprint) => ({
        key: sprint.id.toString(),
        value: sprint.id.toString(),
        placeHolder: `${sprint.name} (${sprint.state})`,
    }));

    const allSelected =
        searchResults.length > 0 && selectedKeys.size === searchResults.length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Text size={TextSize.Large} bold>
                        Batch Add Jira Issues
                    </Text>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    {sprintsLoading ? (
                        <Stack alignItems="center" p={2}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" color="textSecondary" mt={1}>
                                Loading sprints...
                            </Typography>
                        </Stack>
                    ) : (
                        <SelectInput
                            name="Sprint"
                            placeHolder="Select a Sprint..."
                            value={selectedSprint}
                            setValue={setSelectedSprint}
                            options={sprintOptions}
                            includeNone={false}
                            fullWidth
                        />
                    )}

                    {searchLoading && (
                        <Stack alignItems="center" p={2}>
                            <CircularProgress />
                        </Stack>
                    )}

                    {!searchLoading && searchResults.length > 0 && (
                        <Stack spacing={1}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Typography variant="body2" color="textSecondary">
                                    {searchResults.length} issues found, {selectedKeys.size} selected
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={toggleSelectAll}
                                    color="info"
                                >
                                    {allSelected ? "Deselect All" : "Select All"}
                                </Button>
                            </Stack>
                            <List
                                dense
                                sx={{
                                    maxHeight: 400,
                                    overflowY: "auto",
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                }}
                            >
                                {searchResults.map((issue) => {
                                    const isSelected = selectedKeys.has(issue.key);
                                    return (
                                        <ListItem key={issue.key} disablePadding>
                                            <ListItemButton
                                                onClick={() => toggleSelect(issue.key)}
                                                dense
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <Checkbox
                                                        edge="start"
                                                        checked={isSelected}
                                                        color="info"
                                                        size="small"
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="bold"
                                                                sx={{ minWidth: 100 }}
                                                            >
                                                                {issue.key}
                                                            </Typography>
                                                            <Typography variant="body2" noWrap>
                                                                {issue.summary}
                                                            </Typography>
                                                        </Stack>
                                                    }
                                                    secondary={
                                                        <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            mt={0.5}
                                                        >
                                                            <Chip
                                                                label={issue.issueType}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                            <Chip
                                                                label={issue.status}
                                                                size="small"
                                                                color={
                                                                    issue.status === "Done"
                                                                        ? "success"
                                                                        : "default"
                                                                }
                                                            />
                                                            {issue.points !== undefined &&
                                                                issue.points !== null && (
                                                                    <Chip
                                                                        label={`${issue.points} pts`}
                                                                        size="small"
                                                                        color="info"
                                                                        variant="outlined"
                                                                    />
                                                                )}
                                                        </Stack>
                                                    }
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Stack>
                    )}

                    {!searchLoading &&
                        searchResults.length === 0 &&
                        selectedSprint && (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                textAlign="center"
                                p={2}
                            >
                                No issues found in this sprint
                            </Typography>
                        )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} variant="outlined" color="info">
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    color="info"
                    onClick={submit}
                    loading={submitting}
                    disabled={selectedKeys.size === 0 || submitting}
                >
                    Add {selectedKeys.size > 0 ? `(${selectedKeys.size})` : ""}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default BatchAddJiraModal;
